import { labelFromOptions, runScenarioTest } from './shared';

/**
 * @param {object} config
 * @param {string} config.moduleKey
 * @param {Array<{ key: string, label: string, format?: (rule) => string }>} config.summaryFields
 * @param {Array<FieldDef>} config.setupFields
 * @param {Array<FieldDef>} config.inputFields
 * @param {Array<FieldDef>} config.expectedFields
 * @param {(rule, form) => { actual: object, messageShown?: string, nextAction?: string, resultFields?: object }} config.evaluate
 * @param {(form, actual) => Array<{ pass: boolean, reason: string }>} config.compareChecks
 */
export function createModuleSimulator(config) {
  const {
    moduleKey,
    summaryFields,
    setupFields = [],
    inputFields = [],
    expectedFields = [],
    evaluate,
    compareChecks,
  } = config;

  const allFields = [...setupFields, ...inputFields, ...expectedFields];

  function resolveOptions(field, rule) {
    if (typeof field.options === 'function') return field.options(rule) || [];
    return field.options || [];
  }

  function resolveDefault(field, rule) {
    const opts = resolveOptions(field, rule);
    if (typeof field.default === 'function') return field.default(rule, opts);
    if (field.default !== undefined) return field.default;
    if (field.type === 'toggle') return false;
    if (field.type === 'select' && opts.length) return opts[0].value;
    return '';
  }

  function getRuleSummary(rule) {
    if (!rule) return [];
    return summaryFields.map(({ key, label, format }) => ({
      label,
      value: format ? format(rule) : rule[key] || '—',
    }));
  }

  function buildDefaultTestForm(rule) {
    const form = {};
    allFields.forEach((field) => {
      form[field.key] = resolveDefault(field, rule);
    });
    return form;
  }

  function validateTestForm(form, rule) {
    const errors = {};
    allFields.forEach((field) => {
      if (!field.required) return;
      const val = form[field.key];
      if (field.type === 'toggle') return;
      if (val === undefined || val === null || String(val).trim() === '') {
        errors[field.key] = field.errorMessage || `Enter ${field.label.toLowerCase()}.`;
      }
      if (field.type === 'select') {
        const opts = resolveOptions(field, rule);
        if (val && opts.length && !opts.some((o) => o.value === val)) {
          errors[field.key] = `Select a valid ${field.label.toLowerCase()}.`;
        }
      }
    });
    return errors;
  }

  function runTestSimulation(rule, form) {
    return runScenarioTest(
      form,
      () => evaluate(rule, form),
      (f, evaluation) => compareChecks(f, evaluation, rule),
    );
  }

  return {
    moduleKey,
    summaryFields,
    setupFields,
    inputFields,
    expectedFields,
    getRuleSummary,
    buildDefaultTestForm,
    validateTestForm,
    runTestSimulation,
    resolveOptions,
  };
}

export { labelFromOptions };
